import { z } from 'zod';

import { materialPresentationTypeValues } from '../materials-catalog/index.js';
import {
  materialOrderingDefaultModeValues,
  materialOrderingScopeValues,
  materialOrderingSourceValues,
  type GetMaterialOrderingRequest,
  type GetMaterialOrderingResponse,
  type MaterialOrderingDefaultMode,
  type MaterialOrderingItem,
  type MaterialOrderingOverride,
  type MaterialOrderingScope,
  type MaterialOrderingSource,
  type ResolveMaterialOrderingRequest,
  type ResolveMaterialOrderingResponse,
  type ResolvedMaterialOrderingItem,
  type UpsertMaterialOrderingRequest,
  type UpsertMaterialOrderingResponse,
} from './materials-ordering.dto.js';

const timestampSchema = z.iso.datetime();
const dateSchema = z.iso.date();
const orderingSchema = z.number().int().min(0);
const MaterialPresentationTypeSchema = z.enum(materialPresentationTypeValues);
export const MaterialOrderingSourceSchema: z.ZodType<MaterialOrderingSource> = z.enum(
  materialOrderingSourceValues,
);
export const MaterialOrderingScopeSchema: z.ZodType<MaterialOrderingScope> = z.enum(
  materialOrderingScopeValues,
);
export const MaterialOrderingDefaultModeSchema: z.ZodType<MaterialOrderingDefaultMode> = z.enum(
  materialOrderingDefaultModeValues,
);

export const MaterialOrderingItemSchema: z.ZodType<MaterialOrderingItem> = z.object({
  materialId: z.uuid(),
  presentationType: MaterialPresentationTypeSchema,
  sortOrder: orderingSchema,
  isPinned: z.boolean(),
  isHidden: z.boolean(),
});

const orderingItemsSchema = z.array(MaterialOrderingItemSchema).superRefine((items, ctx) => {
  const seen = new Set<string>();
  for (const item of items) {
    const key = `${item.materialId}:${item.presentationType}`;
    if (seen.has(key)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Duplicate materialId and presentationType entries are not allowed.',
      });
      return;
    }
    seen.add(key);
  }
});

export const MaterialOrderingOverrideSchema: z.ZodType<MaterialOrderingOverride> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  stationId: z.uuid().nullable(),
  defaultMode: MaterialOrderingDefaultModeSchema,
  items: orderingItemsSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const GetMaterialOrderingRequestSchema: z.ZodType<GetMaterialOrderingRequest> = z.object({
  companyId: z.uuid(),
  stationId: z.uuid().nullable().optional(),
});

export const GetMaterialOrderingResponseSchema: z.ZodType<GetMaterialOrderingResponse> = z.object({
  ordering: MaterialOrderingOverrideSchema.nullable(),
});

export const UpsertMaterialOrderingRequestSchema: z.ZodType<UpsertMaterialOrderingRequest> = z.object({
  companyId: z.uuid(),
  stationId: z.uuid().nullable().optional(),
  defaultMode: MaterialOrderingDefaultModeSchema.optional(),
  items: orderingItemsSchema,
});

export const UpsertMaterialOrderingResponseSchema: z.ZodType<UpsertMaterialOrderingResponse> = z.object({
  ordering: MaterialOrderingOverrideSchema,
});

export const ResolvedMaterialOrderingItemSchema: z.ZodType<ResolvedMaterialOrderingItem> = z.object({
  materialId: z.uuid(),
  presentationType: MaterialPresentationTypeSchema,
  sortOrder: orderingSchema.nullable(),
  isPinned: z.boolean(),
  isHidden: z.boolean(),
  source: MaterialOrderingSourceSchema,
});

export const ResolveMaterialOrderingRequestSchema: z.ZodType<ResolveMaterialOrderingRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  stationId: z.uuid(),
  effectiveOn: dateSchema.optional(),
  includeInactive: z.boolean().optional(),
  scope: MaterialOrderingScopeSchema.optional(),
});

export const ResolveMaterialOrderingResponseSchema: z.ZodType<ResolveMaterialOrderingResponse> = z.object({
  items: z.array(ResolvedMaterialOrderingItemSchema),
  defaultMode: MaterialOrderingDefaultModeSchema.optional(),
});
